import React, { useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, Eye, EyeOff, Loader, CheckCircle } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import AuthShell from "../components/auth/AuthShell";
import LocationPicker from "../components/LocationPicker";
import { checkAndApplyPendingRole, isEmailAvailable, ensureUserProfileDocument } from "../lib/firebase";
import { updateProfile as updateAuthProfile } from "firebase/auth";
import { createHandleFromEmail, normalizeUsername } from "../utils/userIdentity";
import {
  getPasswordStrengthLabel,
  isValidEmail,
  isValidPhone,
  normalizeEmail,
  normalizePhone,
  validatePasswordStrength,
} from "../utils/authValidation";
import { getFriendlyAuthError } from "../utils/authErrorMessages";
import { errorDev } from "../utils/logger.js";

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2.5 text-sm text-red-700 dark:text-red-300 flex items-start gap-2" role="alert">
      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

function SuccessBanner({ message }) {
  if (!message) return null;
  return (
    <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3 py-2.5 text-sm text-green-800 dark:text-green-300 flex items-start gap-2" role="status">
      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

export default function Signup() {
  const { t, ready: i18nReady } = useTranslation(["auth", "common"]);
  const safeT = (key, fallback) => {
    if (!i18nReady) return fallback || key;
    try {
      const val = t(key);
      return val === key ? fallback || key : val;
    } catch (err) {
      errorDev("Operation failed:", err);
      return fallback || key;
    }
  };

  const {
    user,
    loading,
    signup,
    loginWithGoogle,
    error: authError,
  } = useAuth();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const redirectPath = new URLSearchParams(routeLocation.search).get("redirect") || "/farm";

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [username, setUsername] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [location, setLocation] = useState({
    province: "",
    district: "",
    sector: "",
    cell: "",
  });

  const passwordStrength = useMemo(() => validatePasswordStrength(password), [password]);
  const strengthLabel = useMemo(() => getPasswordStrengthLabel(passwordStrength.score), [passwordStrength.score]);

  const steps = [
    {
      id: 1,
      label: safeT("auth.stepAccount", "Account"),
      subtitle: safeT("auth.step1Subtitle", "Start by adding your email and a secure password"),
    },
    {
      id: 2,
      label: safeT("auth.stepProfile", "Profile"),
      subtitle: safeT("auth.step3Subtitle", "Add a username and location so farmers and communities can find you"),
    },
  ];

  if (!loading && user) {
    return <Navigate to={redirectPath} replace />;
  }

  const validateStep1 = async () => {
    setError("");

    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) {
      setError(safeT("auth.errors.emailRequired", "Email address is required."));
      return false;
    }

    if (!isValidEmail(normalizedEmail)) {
      setError(safeT("auth.errors.invalidEmail", "Please enter a valid email address."));
      return false;
    }

    if (!passwordStrength.isStrong) {
      setError(
        safeT(
          "auth.errors.weakPassword",
          "Password must include uppercase, lowercase, number, symbol, and be at least 8 characters."
        )
      );
      return false;
    }

    if (confirmPassword !== password) {
      setError(safeT("auth.errors.passwordsMismatch", "Passwords do not match."));
      return false;
    }

    try {
      const available = await isEmailAvailable(normalizedEmail);
      if (!available) {
        setError(
          safeT(
            "auth.errors.emailAlreadyInUse",
            "This email is already registered. Sign in or use a different email to continue."
          )
        );
        return false;
      }
    } catch (err) {
      errorDev("Operation failed:", err);
      setSuccess(safeT("auth.emailCheckSkipped", "Could not pre-check email right now. We will verify during account creation."));
    }

    return true;
  };

  const handleContinueStep1 = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    try {
      setBusy(true);
      const valid = await validateStep1();
      if (!valid) return;
      setStep(2);
    } finally {
      setBusy(false);
    }
  };

  const finalizeProfile = async (firebaseUser) => {
    const normalizedProfilePhone = profilePhone ? normalizePhone(profilePhone) : "";
    if (normalizedProfilePhone && !isValidPhone(normalizedProfilePhone)) {
      throw new Error(safeT("auth.errors.phoneRequired", "Enter a valid phone number with country code."));
    }

    const baseName = normalizeUsername(username) || createHandleFromEmail(firebaseUser.email, firebaseUser.uid);

    await updateAuthProfile(firebaseUser, {
      displayName: baseName,
    });

    await ensureUserProfileDocument(firebaseUser, {
      markProfileComplete: true,
      profileData: {
        username: baseName,
        name: baseName,
        displayName: baseName,
        phone: normalizedProfilePhone || null,
        phoneNumber: normalizedProfilePhone || null,
        phoneVerified: false,
        location: location.province
          ? {
              province: location.province || "",
              district: location.district || "",
              sector: location.sector || "",
              cell: location.cell || "",
            }
          : null,
      },
    });
  };

  const handleCompleteSignup = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!normalizeUsername(username)) {
      setError(safeT("auth.errors.usernameRequired", "Username is required."));
      return;
    }

    try {
      setBusy(true);
      const account = await signup(normalizeEmail(email), password);
      const firebaseUser = account?.user;
      if (!firebaseUser?.uid) {
        throw new Error(safeT("auth.errors.signupFailed", "Failed to create account."));
      }

      await finalizeProfile(firebaseUser);

      await checkAndApplyPendingRole(firebaseUser.uid, firebaseUser.email);

      if (account?.verificationEmailSent === false) {
        setSuccess(safeT("auth.signupSuccess", "Welcome to Orrith. Your account has been created successfully."));
        setError(safeT("auth.errors.verificationEmailFailed", "Failed to send verification email. Please try again."));
      } else {
        setSuccess(safeT("auth.signupSuccess", "Welcome to Orrith. Your account has been created successfully."));
      }

      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 900);
    } catch (err) {
      setError(
        authError ||
          getFriendlyAuthError(err, t, {
            defaultKey: "auth.errors.signupFailed",
            defaultMessage: "Sorry, we could not complete your sign up. Please try again.",
          })
      );
    } finally {
      setBusy(false);
    }
  };

  const stepIndicator = (
    <div className="flex justify-between px-1 py-3 mb-6" aria-label="Signup progress" role="tablist">
      {steps.map((s) => {
        const isActive = step === s.id;
        const isCompleted = step > s.id;
        return (
          <div key={s.id} className="flex flex-col items-center gap-2" role="tab" aria-selected={isActive}>
            <span
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-300 ${
                isActive
                  ? "bg-green-700 border-green-700 text-white shadow-[0_0_0_4px_rgba(34,197,94,0.2)]"
                  : isCompleted
                  ? "bg-green-700 border-green-700 text-white"
                  : "bg-gray-50 dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 text-gray-400"
              }`}
              aria-current={isActive ? "true" : undefined}
            >
              {s.id}
            </span>
            <span
              className={`text-xs font-medium text-center leading-snug px-1 ${
                isActive ? "text-green-700 dark:text-green-400" : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {s.label}
            </span>
          </div>
        );
      })}
    </div>
  );

  const fieldInvalid = Boolean(error);

  return (
    <AuthShell
      title={safeT("auth.signup", "Register")}
      subtitle={steps[step - 1]?.subtitle}
      helperLinks={[
        { to: `/login?redirect=${encodeURIComponent(redirectPath)}`, label: safeT("auth.signIn", "Sign In") },
        { to: "/forgot-password", label: safeT("auth.forgotPassword", "Forgot Password") },
      ]}
    >
      {stepIndicator}

      {step === 1 && (
        <form onSubmit={handleContinueStep1} className="space-y-4" noValidate>
          <div>
            <label htmlFor="signup-email" className="block text-sm font-medium text-gray-800 dark:text-gray-100 mb-1.5">
              {safeT("auth.email", "Email Address")}
            </label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              aria-invalid={fieldInvalid}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="ui-input"
            />
          </div>

          <div>
            <label htmlFor="signup-password" className="block text-sm font-medium text-gray-800 dark:text-gray-100 mb-1.5">
              {safeT("auth.password", "Password")}
            </label>
            <div className="relative">
              <input
                id="signup-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                aria-invalid={fieldInvalid}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="ui-input ui-input-icon"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label={showPassword ? safeT("auth.hidePassword", "Hide password") : safeT("auth.showPassword", "Show password")}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="mt-2">
              <div className="grid grid-cols-5 gap-1">
                {[0, 1, 2, 3, 4].map((bar) => (
                  <span
                    key={bar}
                    className={`h-1.5 rounded-full ${bar < Math.min(passwordStrength.score, 5) ? "bg-green-600" : "bg-gray-200 dark:bg-neutral-700"}`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{strengthLabel}</p>
            </div>
          </div>

          <div>
            <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-gray-800 dark:text-gray-100 mb-1.5">
              {safeT("auth.confirmPassword", "Confirm Password")}
            </label>
            <div className="relative">
              <input
                id="signup-confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                aria-invalid={fieldInvalid}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="ui-input ui-input-icon"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label={
                  showConfirmPassword ? safeT("auth.hidePassword", "Hide password") : safeT("auth.showPassword", "Show password")
                }
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && <ErrorBanner message={error} />}
          {success && <SuccessBanner message={success} />}

          <button
            type="submit"
            disabled={busy}
            className="ui-btn-primary w-full py-2.5"
          >
            {busy && <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" aria-hidden="true" />}
            {safeT("common:continue", "Continue")}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-neutral-700" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white dark:bg-neutral-900 text-gray-500 dark:text-gray-400">
                {safeT("auth.orContinueWith", "or continue with")}
              </span>
            </div>
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setError("");
              setSuccess("");

              try {
                setBusy(true);
                const googleUser = await loginWithGoogle();
                await ensureUserProfileDocument(googleUser);

                await checkAndApplyPendingRole(googleUser.uid, googleUser.email);
                navigate("/farm", { replace: true });
              } catch (err) {
                if (err?.code !== "auth/popup-closed-by-user" && err?.code !== "auth/cancelled-popup-request") {
                  if (err?.code === "auth/unauthorized-domain") {
                    setError(safeT("auth.errors.unauthorizedDomain", "This domain is not authorized for Google sign-in."));
                  } else if (err?.code === "auth/popup-blocked") {
                    setError(safeT("auth.errors.popupBlocked", "Popup was blocked by your browser. Allow popups and try again."));
                  } else {
                    setError(
                      authError ||
                        getFriendlyAuthError(err, t, {
                          defaultKey: "auth.errors.signupFailed",
                          defaultMessage: "Sorry, Google sign-up did not complete. Please try again.",
                        })
                    );
                  }
                }
              } finally {
                setBusy(false);
              }
            }}
            className="ui-btn-secondary w-full py-2.5"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {safeT("auth.continueWithGoogle", "Continue with Google")}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleCompleteSignup} className="space-y-4" noValidate>
          <div>
            <label htmlFor="signup-username" className="block text-sm font-medium text-gray-800 dark:text-gray-100 mb-1.5">
              {safeT("auth.username", "Username")}
            </label>
            <input
              id="signup-username"
              type="text"
              autoComplete="username"
              aria-invalid={fieldInvalid}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-xl border border-gray-300 dark:border-neutral-500 bg-white dark:bg-neutral-950 px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {safeT("auth.usernamePlaceholder", "Choose a username")}
            </p>
          </div>

          <div>
            <label htmlFor="signup-profile-phone" className="block text-sm font-medium text-gray-800 dark:text-gray-100 mb-1.5">
              {safeT("auth.profilePhone", "Phone Number")} ({safeT("common:optional", "Optional")})
            </label>
            <input
              id="signup-profile-phone"
              type="tel"
              autoComplete="tel"
              aria-invalid={fieldInvalid}
              value={profilePhone}
              onChange={(e) => setProfilePhone(e.target.value)}
              className="w-full rounded-xl border border-gray-300 dark:border-neutral-500 bg-white dark:bg-neutral-950 px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
              placeholder={safeT("auth.profilePhonePlaceholder", "+250 7XX XXX XXX")}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-gray-100 mb-1.5">
              {safeT("auth.location", "Location")} ({safeT("common:optional", "Optional")})
            </label>
            <LocationPicker value={location} onChange={setLocation} compact={true} />
          </div>

          {error && <ErrorBanner message={error} />}
          {success && <SuccessBanner message={success} />}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-xl bg-green-700 hover:bg-green-800 disabled:bg-gray-400 dark:disabled:bg-neutral-700 text-white font-semibold py-2.5 transition-colors flex items-center justify-center gap-2"
          >
            {busy && <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" aria-hidden="true" />}
            {safeT("auth.completeSignup", "Complete Sign Up")}
          </button>

          <button
            type="button"
            onClick={() => {
              setError("");
              setSuccess("");
              setStep(1);
            }}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 text-gray-700 dark:text-gray-200 py-2.5 font-medium hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
          >
            {safeT("common:back", "Back")}
          </button>

        </form>
      )}
    </AuthShell>
  );
}
